// Modal de "Regerar" do caça-palavras (catálogo de Jogos).
//
// O backend normaliza as palavras (maiúsculas, sem acento) e devolve as que
// EFETIVAMENTE foram posicionadas — pode ser menos do que se enviou. Por isso o modal
// mostra a lista de volta em vez de só dizer "pronto".
import { useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Space, Typography, message } from 'antd';

import { regenerateWordsearch, type JogoAdmin } from '../../services/admin';

interface FormValues {
  palavras: string;
  tamanho?: number;
}

interface Props {
  /** Jogo a regerar; `null` mantém o modal fechado. */
  target: JogoAdmin | null;
  onClose: () => void;
  onDone: () => void;
}

/** Uma palavra por linha; ignora linhas em branco. */
const parsePalavras = (raw: string): string[] =>
  raw
    .split('\n')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

export const RegenerateWordsearchModal: React.FC<Props> = ({ target, onClose, onDone }) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [posicionadas, setPosicionadas] = useState<string[] | null>(null);

  const close = () => {
    setPosicionadas(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!target) return;
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const palavras = parsePalavras(values.palavras);
    if (palavras.length === 0) {
      message.error('Informe ao menos uma palavra.');
      return;
    }

    setSaving(true);
    try {
      const res = await regenerateWordsearch(target.idJogo, {
        palavras,
        ...(values.tamanho != null ? { tamanho: values.tamanho } : {}),
      });
      onDone();
      setPosicionadas(res.palavras);
      message.success('Caça-palavras regerado.');
    } catch (err: any) {
      message.error(err?.response?.data?.msg ?? 'Falha ao regerar o caça-palavras.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={target ? `Regerar caça-palavras #${target.idJogo}` : 'Regerar caça-palavras'}
      visible={target !== null}
      onCancel={close}
      destroyOnClose
      width={560}
      footer={
        posicionadas ? (
          <Button type="primary" onClick={close}>
            Fechar
          </Button>
        ) : (
          <Space>
            <Button onClick={close}>Cancelar</Button>
            <Button type="primary" danger loading={saving} onClick={handleSubmit}>
              Regerar
            </Button>
          </Space>
        )
      }
    >
      {posicionadas ? (
        <Alert
          type="success"
          showIcon
          message={`${posicionadas.length} palavra(s) posicionada(s)`}
          description={
            <>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                O backend normaliza as palavras; estas são as que ficaram na grade:
              </Typography.Paragraph>
              <Typography.Text code>{posicionadas.join(', ')}</Typography.Text>
            </>
          }
        />
      ) : (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="A grade atual será substituída pelas palavras abaixo."
          />
          <Form form={form} layout="vertical" preserve={false}>
            <Form.Item
              name="palavras"
              label="Palavras"
              extra="Uma por linha."
              rules={[{ required: true, message: 'Informe ao menos uma palavra.' }]}
            >
              <Input.TextArea rows={8} placeholder={'SOL\nMAR\nLUA'} />
            </Form.Item>
            <Form.Item
              name="tamanho"
              label="Tamanho da grade"
              extra="Opcional (mínimo 5). Vazio deixa o backend escolher."
            >
              <InputNumber min={5} placeholder="—" />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default RegenerateWordsearchModal;
